import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  CircleAlert,
  CircleDollarSign,
  Clock3,
  Gauge,
  LoaderCircle,
  Play,
  RotateCcw,
  Server,
  ShoppingBag,
  TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatUsd } from "../utils/money";
import {
  marketplaceApi,
  resolveMediaUrl,
  type UserMiner,
} from "../api/marketplace";
import { useCopy } from "../i18n/copy";

function MinerCountdown({ miner }: { miner: UserMiner }) {
  const { tr } = useCopy();
  const initialSeconds =
    Math.max(0, Number(miner.day || 0)) * 86400 +
    Math.max(0, Number(miner.hour || 0)) * 3600 +
    Math.max(0, Number(miner.minute || 0)) * 60 +
    Math.max(0, Number(miner.second || 0));
  const [remaining, setRemaining] = useState(initialSeconds);

  useEffect(() => {
    setRemaining(initialSeconds);
    const timer = window.setInterval(
      () => setRemaining((value) => Math.max(0, value - 1)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [initialSeconds]);

  if (remaining === 0) return null;
  const hours = Math.floor(remaining / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;
  return (
    <span className="miner-countdown">
      <Clock3 />
      <small>{tr("Próximo ingreso")}</small>
      {String(hours).padStart(2, "0")}h {String(minutes).padStart(2, "0")}m{" "}
      {String(seconds).padStart(2, "0")}s
    </span>
  );
}

export function MinersPage() {
  const { tr } = useCopy();
  const client = useQueryClient();
  const [state, setState] = useState<1 | 2>(1);
  const [confirm, setConfirm] = useState<{
    action: "start" | "sell";
    miner: UserMiner;
  } | null>(null);
  const query = useQuery({
    queryKey: ["user-miners", state],
    queryFn: () => marketplaceApi.userMiners(state),
  });
  const action = useMutation({
    mutationFn: () =>
      confirm?.action === "sell"
        ? marketplaceApi.sell(confirm.miner.id)
        : marketplaceApi.start(confirm!.miner.id),
    onSuccess: () => {
      setConfirm(null);
      client.invalidateQueries({ queryKey: ["user-miners"] });
      client.invalidateQueries({ queryKey: ["session-user"] });
    },
  });
  const items = query.data || [];
  const today = items.reduce(
    (sum, item) => sum + Number(item.today_income || 0),
    0,
  );
  const total = items.reduce(
    (sum, item) => sum + Number(item.total_income || 0),
    0,
  );
  return (
    <main className="page">
      <header className="page-title-row">
        <div>
          <p className="eyebrow">{tr("Centro de operación")}</p>
          <h1>{tr("Mis equipos")}</h1>
        </div>
      </header>
      <div className="summary-strip">
        <span>
          <Server />
          <small>{tr("Equipos activos")}</small>
          <strong>{items.length}</strong>
        </span>
        <span>
          <CircleDollarSign />
          <small>{tr("Ingreso de hoy")}</small>
          <strong>{formatUsd(today)}</strong>
        </span>
        <span>
          <TrendingUp />
          <small>{tr("Ingreso acumulado")}</small>
          <strong>{formatUsd(total)}</strong>
        </span>
      </div>
      <div className="segmented miner-state-tabs">
        <button
          className={state === 1 ? "active" : ""}
          onClick={() => setState(1)}
        >
          {tr("En marcha")}
        </button>
        <button
          className={state === 2 ? "active" : ""}
          onClick={() => setState(2)}
        >
          {tr("Finalizados")}
        </button>
      </div>
      {query.isLoading && (
        <div className="loading-row">
          <LoaderCircle className="spin" /> {tr("Cargando equipos…")}
        </div>
      )}
      {query.isError && (
        <div className="form-feedback error">
          <CircleAlert /> {tr("No pudimos cargar tus equipos.")}
        </div>
      )}
      <div className="miner-list">
        {items.map((miner) => (
          <article className="miner-row" key={miner.id}>
            <div
              className={`miner-thumb ${miner.start_status === 2 ? "running" : "pending"}`}
            >
              {miner.img_full || miner.img ? (
                <img
                  src={resolveMediaUrl(miner.img_full || miner.img)}
                  alt={`${tr("Equipo")} ${miner.name}`}
                />
              ) : (
                <Server aria-hidden="true" />
              )}
            </div>
            <div className="miner-copy">
              <span
                className={`status-chip ${miner.start_status === 2 ? "" : "pending"}`}
              >
                <i />
                {tr(
                  miner.start_status === 2
                    ? "En funcionamiento"
                    : "Por activar",
                )}
              </span>
              <h2>{miner.name}</h2>
              <div className="miner-value-grid">
                <span>
                  <small>{tr("Precio de compra")}</small>
                  <strong>{formatUsd(Number(miner.prize || 0))}</strong>
                </span>
                <span>
                  <small>{tr("Ingreso de hoy")}</small>
                  <strong>{formatUsd(Number(miner.today_income || 0))}</strong>
                </span>
                <span>
                  <small>{tr("Ingreso acumulado")}</small>
                  <strong>{formatUsd(Number(miner.total_income || 0))}</strong>
                </span>
                <span>
                  <small>{tr("Días transcurridos")}</small>
                  <strong>
                    {miner.workDay ?? "—"} {tr("días")}
                  </strong>
                </span>
              </div>
              <div className="miner-inline-stats">
                <span>
                  <Gauge /> {miner.computing_power || 0} TH/s
                </span>
                {miner.create_time_r && (
                  <span>
                    <CalendarDays /> {miner.create_time_r}
                  </span>
                )}
                {miner.expiration_time && (
                  <span>
                    <Clock3 /> {miner.expiration_time}
                  </span>
                )}
              </div>
              {miner.start_status === 2 && (
                <MinerCountdown miner={miner} />
              )}
            </div>
            <div className="miner-actions">
              {state === 1 && Number(miner.type) !== 1 && (
                <button
                  className="button secondary"
                  onClick={() => setConfirm({ action: "sell", miner })}
                >
                  <RotateCcw /> {tr("Vender")}
                </button>
              )}
              <button
                className="button primary"
                disabled={miner.start_status === 2 || state === 2}
                onClick={() => setConfirm({ action: "start", miner })}
              >
                {miner.start_status === 2 ? (
                  <>
                    <BadgeCheck /> {tr("Activo")}
                  </>
                ) : (
                  <>
                    <Play /> {tr("Activar")}
                  </>
                )}
              </button>
            </div>
          </article>
        ))}
      </div>
      {!query.isLoading && !items.length && (
        <div className="empty-state">
          <ShoppingBag />
          <h2>
            {tr(
              state === 1
                ? "No tienes equipos activos"
                : "No tienes equipos finalizados",
            )}
          </h2>
          <Link className="button primary" to="/market">
            {tr("Explorar mercado")} <ArrowRight />
          </Link>
        </div>
      )}
      {confirm && (
        <div
          className="action-sheet-backdrop"
          role="presentation"
          onClick={() => setConfirm(null)}
        >
          <section
            className="action-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="miner-confirm-title"
            onClick={(event) => event.stopPropagation()}
          >
            <span>{confirm.action === "start" ? <Play /> : <RotateCcw />}</span>
            <h2 id="miner-confirm-title">
              {tr(
                confirm.action === "start"
                  ? "Confirmar activación"
                  : "Confirmar venta",
              )}
            </h2>
            <p>{confirm.miner.name}</p>
            {action.isError && (
              <div className="form-feedback error">
                <CircleAlert /> {action.error.message}
              </div>
            )}
            <div>
              <button
                className="button secondary"
                onClick={() => setConfirm(null)}
              >
                {tr("Cancelar")}
              </button>
              <button
                className="button primary"
                disabled={action.isPending}
                onClick={() => action.mutate()}
              >
                {action.isPending ? (
                  <LoaderCircle className="spin" />
                ) : confirm.action === "start" ? (
                  <Play />
                ) : (
                  <RotateCcw />
                )}{" "}
                {tr("Confirmar")}
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
